/**
 * Views menu — BottomSheet below-sm (DirectoryFilterSheet peer ONLY) / anchored panel sm-up.
 * ADR-MV-01 / EV-G25-STATIC-ARIA-DTO. OverlayBackDismiss when open (LIFO under wizard → Confirm).
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BREAKPOINTS } from 'pi-kiosk-shared/responsive';
import { SIGN_IN_TO_SAVE_VIEWS_TEST_ID } from '../../account/mapViewSignInGate.js';
import { useOptionalOverlayBackDismiss } from '../../../shared/context/OverlayBackDismissContext.js';
import { BottomSheet } from '../../../shared/ui/BottomSheet.js';
import { Button } from '../../../shared/ui/Button.js';
import { ConfirmDialog } from '../../../shared/ui/ConfirmDialog.js';
import { Dialog } from '../../../shared/ui/Dialog.js';
import {
  defaultRadiusKmDraft,
  radiusErrorMessageKey,
  ShopMapRadiusEditor,
  validateRadiusKmDraft,
} from './ShopMapRadiusEditor.js';
import {
  parseRadiusKm,
  type ShopMapBuiltinId,
  type ShopMapSavedViewRow,
} from '../shopMapViewModel.js';

/** Peer DirectoryListFilters — Tailwind sm = BREAKPOINTS.sm (640). */
const SM_UP_MQ = `(min-width: ${String(BREAKPOINTS.sm)}px)`;

/** ADR-MV-01 — gap under Views trigger; viewport pad (OverflowMenu peer). */
const PANEL_ANCHOR_GAP_PX = 4;
const PANEL_VIEWPORT_PAD_PX = 8;
const PANEL_MIN_WIDTH_PX = 256; // min-w-[16rem]

const MENU_ROW_CLASS = [
  'flex w-full min-h-11 items-center gap-2 rounded-md px-3 py-2 text-left text-sm',
  'text-[var(--color-on-surface)]',
  'hover:bg-[var(--color-surface-hover)]',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
].join(' ');

function useSmUp(): boolean {
  const [smUp, setSmUp] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(SM_UP_MQ).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia(SM_UP_MQ);
    const onChange = (): void => {
      setSmUp(media.matches);
    };
    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, []);

  return smUp;
}

export interface ShopMapViewsMenuProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Trigger element for sm-up panel anchor (outside Leaflet isolate). */
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly checkedBuiltin: ShopMapBuiltinId | null;
  readonly activeViewId: number | null;
  readonly ephemeralAroundMeActive: boolean;
  readonly savedViews: readonly ShopMapSavedViewRow[];
  readonly signedIn: boolean;
  readonly onSelectGeneral: () => void;
  readonly onSelectMyLocation: () => void;
  readonly onApplyAroundMe: (radiusKm: number) => void;
  readonly onApplySavedView: (viewId: number) => void;
  readonly onAddCustomView: () => void;
  readonly onSaveThisView: () => void;
  /**
   * Guest CTA — return true when park succeeded and parent is navigating;
   * false when park failed (menu must stay open). G6 fail-closed.
   */
  readonly onSignInToSaveViews: () => boolean;
  /** G6 — park-null feedback while menu remains open. */
  readonly signInParkFailed?: boolean;
  readonly onRequestDeleteView: (viewId: number) => void;
  readonly onRequestRenameView?: (
    viewId: number,
    name: string,
  ) => Promise<void>;
  readonly deletePending?: boolean;
  readonly renamePending?: boolean;
}

export function ShopMapViewsMenu(props: ShopMapViewsMenuProps): JSX.Element | null {
  const {
    open,
    onClose,
    checkedBuiltin,
    activeViewId,
    ephemeralAroundMeActive,
    savedViews,
    signedIn,
    onSelectGeneral,
    onSelectMyLocation,
    onApplyAroundMe,
    onApplySavedView,
    onAddCustomView,
    onSaveThisView,
    onSignInToSaveViews,
    signInParkFailed = false,
    onRequestDeleteView,
    onRequestRenameView,
    deletePending = false,
    renamePending = false,
  } = props;

  const { t } = useTranslation('customer');
  const smUp = useSmUp();
  const titleId = useId();
  const renameTitleId = useId();
  const menuId = 'customer-shops-directory-views-menu';
  const { anchorRef } = props;
  const panelRef = useRef<HTMLDivElement>(null);
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;

  const [aroundMeExpanded, setAroundMeExpanded] = useState(false);
  const [radiusDraft, setRadiusDraft] = useState(() => defaultRadiusKmDraft());
  const [radiusTouched, setRadiusTouched] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [renameTargetId, setRenameTargetId] = useState<number | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);

  const radiusError =
    radiusTouched || aroundMeExpanded
      ? validateRadiusKmDraft(radiusDraft)
      : null;
  const radiusInvalidMessage =
    radiusError !== null ? t(radiusErrorMessageKey(radiusError)) : null;

  /** ADR-MV-01 — fixed chrome only; top/left placed from trigger rect (no hardcoded 72/16). */
  const panelStyle: CSSProperties | undefined =
    open && smUp
      ? {
          position: 'fixed',
          zIndex: 50,
          maxWidth: 'min(22rem, calc(100vw - 1.5rem))',
        }
      : undefined;

  /** G4 — rename Dialog / delete Confirm sit above Views chrome (sheet or panel). */
  const nestedModalOpen =
    deleteTargetId !== null || renameTargetId !== null;

  const handleClose = useCallback((): void => {
    // G4 — BottomSheet Escape/backdrop call onClose without topmost check; suppress
    // while rename/delete own the stack so Views does not dismiss underneath.
    if (deleteTargetId !== null || renameTargetId !== null) {
      return;
    }
    setAroundMeExpanded(false);
    setRadiusDraft(defaultRadiusKmDraft());
    setRadiusTouched(false);
    setDeleteTargetId(null);
    setRenameTargetId(null);
    setRenameDraft('');
    setRenameError(null);
    onClose();
  }, [onClose, deleteTargetId, renameTargetId]);

  const handleRenameClose = useCallback((): void => {
    if (renamePending) {
      return;
    }
    setRenameTargetId(null);
    setRenameDraft('');
    setRenameError(null);
  }, [renamePending]);

  const handleDeleteCancel = useCallback((): void => {
    if (deletePending) {
      return;
    }
    setDeleteTargetId(null);
  }, [deletePending]);

  // G4 — menu OverlayBackDismiss only while no nested rename/delete (Confirm registers itself).
  useEffect(() => {
    if (
      !open ||
      nestedModalOpen ||
      registerOverlayDismiss === undefined
    ) {
      return;
    }
    return registerOverlayDismiss(handleClose);
  }, [open, nestedModalOpen, handleClose, registerOverlayDismiss]);

  // G4 — rename Dialog owns OverlayBackDismiss LIFO above the menu (Escape/back first).
  useEffect(() => {
    if (renameTargetId === null || registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(handleRenameClose);
  }, [renameTargetId, handleRenameClose, registerOverlayDismiss]);

  useEffect(() => {
    if (!open || !smUp) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      // G4 — delete Confirm + rename Dialog own Escape; do not close menu under them.
      if (event.key === 'Escape' && !nestedModalOpen) {
        event.stopPropagation();
        handleClose();
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => {
      window.removeEventListener('keydown', onKey, { capture: true });
    };
  }, [open, smUp, handleClose, nestedModalOpen]);

  // ADR-MV-01 — place sm-up panel under Views trigger via getBoundingClientRect.
  // Imperative layout (no setState) avoids react-hooks/set-state-in-effect.
  useLayoutEffect(() => {
    if (!open || !smUp) {
      return;
    }
    const panel = panelRef.current;
    const anchor = anchorRef.current;
    if (panel === null || anchor === null) {
      return;
    }

    const placePanel = (): void => {
      const rect = anchor.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      // Clamp into shell chrome band (header bottom → bottom-nav top) so short
      // landscape cannot park the panel over fixed chrome (P12/P13 assert).
      const header = document.querySelector(
        '[data-testid="customer-shell-header"]',
      );
      const nav = document.querySelector('[data-testid="customer-bottom-nav"]');
      const headerBottom =
        header instanceof HTMLElement
          ? header.getBoundingClientRect().bottom
          : 0;
      const navTop =
        nav instanceof HTMLElement && nav.getClientRects().length > 0
          ? nav.getBoundingClientRect().top
          : viewportHeight;
      const chromeTop = Math.max(
        PANEL_VIEWPORT_PAD_PX,
        Math.round(headerBottom) + PANEL_VIEWPORT_PAD_PX,
      );
      const chromeBottom = Math.min(
        viewportHeight - PANEL_VIEWPORT_PAD_PX,
        Math.round(navTop) - PANEL_VIEWPORT_PAD_PX,
      );
      const maxPanelHeight = Math.max(48, chromeBottom - chromeTop);

      const maxWidth = Math.max(
        0,
        viewportWidth - 2 * PANEL_VIEWPORT_PAD_PX,
      );
      const measuredWidth = Math.max(
        panel.offsetWidth,
        Math.min(PANEL_MIN_WIDTH_PX, maxWidth),
      );
      const panelWidth = Math.min(measuredWidth, maxWidth);
      panel.style.maxHeight = `${String(maxPanelHeight)}px`;
      const panelHeight = Math.min(panel.offsetHeight, maxPanelHeight);

      let left = rect.right - panelWidth;
      left = Math.max(
        PANEL_VIEWPORT_PAD_PX,
        Math.min(left, viewportWidth - panelWidth - PANEL_VIEWPORT_PAD_PX),
      );

      let top = rect.bottom + PANEL_ANCHOR_GAP_PX;
      if (panelHeight > 0 && top + panelHeight > chromeBottom) {
        const above = rect.top - PANEL_ANCHOR_GAP_PX - panelHeight;
        if (above >= chromeTop) {
          top = above;
        } else {
          top = Math.max(chromeTop, chromeBottom - panelHeight);
        }
      }
      top = Math.max(chromeTop, Math.min(top, chromeBottom - panelHeight));

      panel.style.top = `${String(top)}px`;
      panel.style.left = `${String(left)}px`;
      panel.style.right = 'auto';
    };

    placePanel();
    const rafId = window.requestAnimationFrame(placePanel);
    window.addEventListener('resize', placePanel);
    window.addEventListener('scroll', placePanel, { capture: true });
    return (): void => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', placePanel);
      window.removeEventListener('scroll', placePanel, { capture: true });
    };
  }, [open, smUp, anchorRef, savedViews.length, aroundMeExpanded]);

  const handleApplyAroundMe = useCallback((): void => {
    setRadiusTouched(true);
    const parsed = parseRadiusKm(radiusDraft);
    if (!parsed.ok) {
      return;
    }
    onApplyAroundMe(parsed.value);
    setAroundMeExpanded(false);
    handleClose();
  }, [onApplyAroundMe, handleClose, radiusDraft]);

  const deleteTarget =
    deleteTargetId === null
      ? null
      : (savedViews.find((row) => row.id === deleteTargetId) ?? null);

  const renameTarget =
    renameTargetId === null
      ? null
      : (savedViews.find((row) => row.id === renameTargetId) ?? null);

  const handleRenameConfirm = useCallback(async (): Promise<void> => {
    if (
      renameTargetId === null ||
      onRequestRenameView === undefined ||
      renamePending
    ) {
      return;
    }
    const trimmed = renameDraft.trim();
    if (trimmed.length === 0 || trimmed.length > 64) {
      setRenameError(t('directory.mapView.nameHint'));
      return;
    }
    setRenameError(null);
    try {
      await onRequestRenameView(renameTargetId, trimmed);
      setRenameTargetId(null);
      setRenameDraft('');
    } catch {
      setRenameError(t('directory.mapView.nameConflict'));
    }
  }, [
    onRequestRenameView,
    renameDraft,
    renamePending,
    renameTargetId,
    t,
  ]);

  const menuBody: ReactNode = (
    <div
      id={menuId}
      role="menu"
      aria-label={t('directory.mapView.viewsMenuAria')}
      data-testid={menuId}
      className="flex flex-col gap-1"
    >
      <button
        type="button"
        role="menuitemradio"
        aria-checked={checkedBuiltin === 'general'}
        className={MENU_ROW_CLASS}
        data-testid="customer-shops-directory-view-general"
        onClick={() => {
          onSelectGeneral();
          handleClose();
        }}
      >
        {t('directory.mapView.generalView')}
      </button>

      <button
        type="button"
        role="menuitemradio"
        aria-checked={checkedBuiltin === 'myLocation'}
        className={MENU_ROW_CLASS}
        data-testid="customer-shops-directory-view-my-location"
        onClick={() => {
          onSelectMyLocation();
          handleClose();
        }}
      >
        {t('directory.mapView.myLocation')}
      </button>

      <button
        type="button"
        role="menuitemradio"
        aria-checked={checkedBuiltin === 'aroundMe'}
        className={MENU_ROW_CLASS}
        data-testid="customer-shops-directory-view-around-me"
        onClick={() => {
          setAroundMeExpanded((prev) => !prev);
        }}
      >
        {t('directory.mapView.aroundMeLabel')}
      </button>

      {aroundMeExpanded ? (
        <div className="flex flex-col gap-2 border-t border-[var(--color-border)] px-1 py-2">
          <ShopMapRadiusEditor
            value={radiusDraft}
            onChange={(next) => {
              setRadiusDraft(next);
              setRadiusTouched(true);
            }}
            label={t('directory.mapView.aroundMeKmLabel')}
            ariaLabel={t('directory.mapView.aroundMeKmAria')}
            invalidMessage={radiusInvalidMessage}
            testId="customer-shops-directory-around-me-km"
          />
          <Button
            type="button"
            intent="primary"
            size="md"
            block
            data-testid="customer-shops-directory-around-me-apply"
            aria-label={t('directory.mapView.aroundMeApplyAria')}
            onClick={handleApplyAroundMe}
          >
            {t('directory.mapView.aroundMeApply')}
          </Button>
        </div>
      ) : null}

      {savedViews.length > 0 ? (
        <ul className="mt-1 flex flex-col gap-1 border-t border-[var(--color-border)] pt-2">
          {savedViews.map((view) => (
            <li key={view.id} className="flex flex-col gap-1">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={activeViewId === view.id}
                aria-label={t('directory.mapView.savedViewAria', {
                  name: view.name,
                })}
                className={MENU_ROW_CLASS}
                data-testid={`customer-shops-directory-view-row-${String(view.id)}`}
                onClick={() => {
                  onApplySavedView(view.id);
                  handleClose();
                }}
              >
                {view.name}
              </button>
              <div className="flex gap-1 px-2 pb-1">
                {onRequestRenameView !== undefined ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="min-h-11 rounded-md px-2 text-xs text-[var(--color-on-surface)] hover:bg-[var(--color-surface-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
                    data-testid={`customer-shops-directory-view-rename-${String(view.id)}`}
                    onClick={() => {
                      setRenameTargetId(view.id);
                      setRenameDraft(view.name);
                      setRenameError(null);
                    }}
                  >
                    {t('directory.mapView.renameView')}
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  className="min-h-11 rounded-md px-2 text-xs text-[var(--color-danger)] hover:bg-[var(--color-surface-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
                  data-testid={`customer-shops-directory-view-delete-${String(view.id)}`}
                  onClick={() => {
                    setDeleteTargetId(view.id);
                  }}
                >
                  {t('directory.mapView.deleteView')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {signedIn ? (
        <button
          type="button"
          role="menuitem"
          className={`${MENU_ROW_CLASS} mt-1 border-t border-[var(--color-border)] pt-2`}
          data-testid="customer-shops-directory-views-add"
          onClick={() => {
            onAddCustomView();
            handleClose();
          }}
        >
          {t('directory.mapView.addCustomView')}
        </button>
      ) : (
        <>
          <button
            type="button"
            role="menuitem"
            className={`${MENU_ROW_CLASS} mt-1 border-t border-[var(--color-border)] pt-2`}
            data-testid={SIGN_IN_TO_SAVE_VIEWS_TEST_ID}
            onClick={() => {
              const parked = onSignInToSaveViews();
              if (parked) {
                handleClose();
              }
            }}
          >
            {t('directory.mapView.signInToSaveViews')}
          </button>
          {signInParkFailed ? (
            <p
              role="status"
              aria-live="polite"
              className="mt-1 px-3 text-sm text-[var(--color-on-surface-muted)]"
              data-testid="customer-shops-directory-sign-in-park-failed"
            >
              {t('directory.mapView.signInParkFailed', {
                defaultValue:
                  'Could not save your return to the map. Stay here and try Sign in again.',
              })}
            </p>
          ) : null}
        </>
      )}

      {ephemeralAroundMeActive && signedIn ? (
        <button
          type="button"
          role="menuitem"
          className={MENU_ROW_CLASS}
          data-testid="customer-shops-directory-views-save-this"
          onClick={() => {
            onSaveThisView();
            handleClose();
          }}
        >
          {t('directory.mapView.saveThisView')}
        </button>
      ) : null}
    </div>
  );

  const confirm = (
    <>
      <ConfirmDialog
        open={deleteTarget !== null}
        title={t('directory.mapView.confirmDeleteTitle')}
        description={t('directory.mapView.confirmDeleteBody', {
          name: deleteTarget?.name ?? '',
        })}
        confirmLabel={t('directory.mapView.deleteView')}
        cancelLabel={t('directory.mapView.wizardCancel')}
        confirmIntent="danger"
        pending={deletePending}
        testId="customer-shops-directory-views-delete-confirm"
        onCancel={handleDeleteCancel}
        onConfirm={() => {
          if (deletePending) {
            return;
          }
          if (deleteTargetId !== null) {
            onRequestDeleteView(deleteTargetId);
          }
          setDeleteTargetId(null);
        }}
      />
      <Dialog
        open={renameTarget !== null}
        onClose={handleRenameClose}
        title={t('directory.mapView.renameView')}
        titleId={renameTitleId}
        closeLabel={t('directory.mapView.wizardCancel')}
        testId="customer-shops-directory-views-rename"
        className="max-w-sm"
        shellClassName="lg:items-center"
        pending={renamePending}
      >
        <label className="flex min-w-0 flex-col gap-1 text-xs text-[var(--color-on-surface-muted)]">
          <span>{t('directory.mapView.nameLabel')}</span>
          <input
            type="text"
            value={renameDraft}
            maxLength={64}
            data-testid="customer-shops-directory-views-rename-input"
            className="min-h-11 w-full rounded-sm border border-[var(--color-border-strong)] bg-[var(--color-surface-elevated)] px-3 py-2 text-sm text-[var(--color-on-surface)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
            onChange={(event) => {
              setRenameDraft(event.target.value);
              setRenameError(null);
            }}
          />
        </label>
        {renameError !== null ? (
          <p className="mt-2 text-sm text-[var(--color-danger)]" role="alert">
            {renameError}
          </p>
        ) : null}
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            intent="secondary"
            type="button"
            onClick={handleRenameClose}
          >
            {t('directory.mapView.wizardCancel')}
          </Button>
          <Button
            intent="primary"
            type="button"
            loading={renamePending}
            data-testid="customer-shops-directory-views-rename-save"
            onClick={() => {
              void handleRenameConfirm();
            }}
          >
            {t('directory.mapView.renameView')}
          </Button>
        </div>
      </Dialog>
    </>
  );

  if (!open && deleteTarget === null && renameTarget === null) {
    return null;
  }

  let chrome: ReactNode = null;
  if (open) {
    if (!smUp) {
      // ADR-MV-01 — peer DirectoryFilterSheet ONLY (BottomSheet + same sheet chrome classes).
      chrome = (
        <BottomSheet
          open={open}
          onClose={handleClose}
          title={t('directory.mapView.viewsTrigger')}
          titleId={titleId}
          closeLabel={t('shared.cancel')}
          testId="customer-shops-directory-views-sheet"
          className="max-h-[90vh] bg-[var(--color-surface-elevated)] p-5 lg:max-w-lg"
          containerClassName="lg:items-center"
        >
          {menuBody}
          <div className="mt-4">
            <Button
              intent="primary"
              size="md"
              block
              type="button"
              data-testid="customer-shops-directory-views-sheet-done"
              onClick={handleClose}
            >
              {t('directory.filters.done')}
            </Button>
          </div>
        </BottomSheet>
      );
    } else {
      chrome = (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-transparent"
            aria-label={t('shared.cancel')}
            data-testid="customer-shops-directory-views-panel-backdrop"
            onClick={handleClose}
          />
          <div
            ref={panelRef}
            style={panelStyle}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="z-50 min-w-[16rem] overflow-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-3 shadow-[var(--shadow-popover)]"
            data-testid="customer-shops-directory-views-panel"
          >
            <p
              id={titleId}
              className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-on-surface-muted)]"
            >
              {t('directory.mapView.viewsTrigger')}
            </p>
            {menuBody}
          </div>
        </>
      );
    }
  }

  return (
    <>
      {chrome}
      {confirm}
    </>
  );
}
